'use client'

import { useEffect, useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import {
  CallRecordingPlayer,
  type CallRecordingPlayerHandle,
} from '@/features/cases/components/call-recording-player'
import { CallTranscript } from '@/features/cases/components/call-transcript'
import { useUpdateCallNotes } from '@/features/cases/hooks/use-case-subresource-writes'
import { callOutcomeLabel, type CallSummary } from '@/features/cases/types'
import { formatDateTime } from '@/lib/format/date'

/**
 * Click-through detail view for one call-log card — full (scrollable, editable) notes and, for a
 * real Plivo call, the seekable recording player. One Dialog per call, matching this codebase's
 * own established "edit X in a Dialog" convention (`CaseDetailsDialog` etc.) rather than an
 * inline-edit widget, which has no precedent here.
 */
export function CallDetailDialog({
  call,
  caseId,
  open,
  onOpenChange,
}: {
  call: CallSummary
  caseId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [notes, setNotes] = useState(call.notes ?? '')
  const updateNotes = useUpdateCallNotes(caseId)
  const playerRef = useRef<CallRecordingPlayerHandle>(null)

  // Re-syncs when a different call's dialog opens (or this call's notes change after a save) —
  // `useState`'s initializer alone only runs once per mount, and the parent may reuse one dialog
  // instance across different `call` rows rather than remounting per card.
  useEffect(() => {
    setNotes(call.notes ?? '')
  }, [call.id, call.notes])

  const onSaveNotes = () => {
    updateNotes.mutate({ callId: call.id, notes })
  }

  const durationMinutes = Math.round(call.durationSec / 60)
  const notesChanged = notes !== (call.notes ?? '')

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setNotes(call.notes ?? '')
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader className="gap-3">
          <DialogTitle className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <Badge variant="outline">{callOutcomeLabel(call.outcome)}</Badge>
            <span className="text-muted-foreground text-sm font-normal">
              {formatDateTime(call.startedAt)} · {durationMinutes} min
            </span>
          </DialogTitle>
          <DialogDescription>Call with {call.investorName ?? 'the investor'}.</DialogDescription>
        </DialogHeader>

        {call.hasRecording && (
          <div>
            <FieldLabel>Recording</FieldLabel>
            <CallRecordingPlayer ref={playerRef} callId={call.id} />
          </div>
        )}

        {call.hasRecording &&
          (call.transcript ? (
            <CallTranscript
              transcript={call.transcript}
              onSeek={(seconds) => void playerRef.current?.seekTo(seconds)}
            />
          ) : (
            <div>
              <FieldLabel>Transcript</FieldLabel>
              {call.transcriptError ? (
                <p className="text-destructive text-sm">Transcription failed.</p>
              ) : (
                <p className="text-muted-foreground text-sm">Transcript pending…</p>
              )}
            </div>
          ))}

        <Field>
          <FieldLabel htmlFor="call-detail-notes">Notes</FieldLabel>
          {/* `Textarea`'s base styles use `field-sizing-content` (auto-grows to fit its value)
              with no cap of its own — capped here so long notes scroll inside the textarea
              itself instead of growing the dialog past the viewport. */}
          <Textarea
            id="call-detail-notes"
            rows={8}
            className="max-h-64 resize-none overflow-y-auto"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What was covered on this call?"
          />
        </Field>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Close
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={onSaveNotes}
            disabled={updateNotes.isPending || !notesChanged}
          >
            {updateNotes.isPending ? 'Saving…' : 'Save notes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
